import React from "react";
import { onCreateSubmit, useApplyCreate } from "@hex-insights/app-modules";
import {
	Button,
	ButtonWithConfirmation,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	Paragraph,
	pickKeys,
	Row,
	Rule,
	Section,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import {
	anyFieldChanged,
	FieldIf,
	Form,
	FormState,
	FormSubmitFunction,
	SubFormField,
	SubFormFieldItemsProps,
	SubFormRenderProps,
	SubmitButton,
	useFormState,
	useInternalField,
	useOnSuccess,
} from "@hex-insights/forms";
import { Table } from "@hex-insights/tables";
import {
	AcademicYear,
	ClassYearNameFragment,
	Student,
	StudentEnrollmentIntent,
	StudentNameAndImageFragment,
	useCreateOrUpdateBulkStudentEnrollmentIntentsMutation,
} from "../../../../../Utilities";
import { ClassYearFormat, StudentFormat } from "../../../../Format";
import { StudentEnrollmentIntentField } from "../../../../Forms";
import { StudentEnrollmentIntentFormConversion, StudentEnrollmentIntentFormValues } from "../../../../FormUtilities";
import { StudentUtilities } from "../../../../Utilities";
import { StudentIcon } from "../../../Student/Icon";
import { ReEnrollmentMealPlanContent } from "../MealPlan";
import { tableColumns, tableColumnWidths, useStudentTuitionText } from "../utilities";
import generalStyles from "../styles.module.css";

export type ReEnrollmentFormValues = {
	students: ReEnrollmentStudentFormValues[];
} & Pick<StudentEnrollmentIntentFormValues.Create, "academicYearID" | "isInterestedInMealPlan">;

export type ReEnrollmentStudentFormValues = Pick<
	StudentEnrollmentIntentFormValues.Create,
	"studentID" | "isEnrolling" | "declineReason" | "declineReasonDescription"
>;

export type ReEnrollmentFormProps = {
	students: (Pick<Student, "id" | "graduationYear"> &
		StudentNameAndImageFragment & {
			studentEnrollmentIntents: Pick<
				StudentEnrollmentIntent,
				"id" | "isEnrolling" | "isInterestedInMealPlan" | "declineReason" | "declineReasonDescription"
			>[];
		})[];
	academicYear: Pick<AcademicYear, "id">;
	classYears: Record<number, ClassYearNameFragment>;
	isWide: boolean;
	onCancel: () => void;
};

export function ReEnrollmentForm({ students, academicYear, classYears, isWide, onCancel }: ReEnrollmentFormProps) {
	const formState = useFormState({
		initialFormValues: () => getInitialReEnrollmentFormValues(students, academicYear, classYears),
	});
	const create = useCreateOrUpdate();
	const { applyCreate } = useApplyCreate(create);
	const onSubmit = React.useCallback<FormSubmitFunction<ReEnrollmentFormValues>>(
		async ({ formValues }) => onCreateSubmit(formValues, applyCreate),
		[applyCreate],
	);
	useOnSuccess(formState, onCancel);

	useInternalField(formState, "academicYearID");

	return (
		<React.Fragment>
			<Modal.Body className={generalStyles["modal__body"]}>
				<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)}>
					<Section>
						<Section.Header>
							<Heading.H3 noMargin>Re-Enroll Students</Heading.H3>
						</Section.Header>
						<Section.Body>
							<Paragraph className={generalStyles["intro-paragraph"]}>
								Please specify the students you would like to re-enroll for the 2024-2025 school year. Tuition won't be
								due until later.
							</Paragraph>

							<Column justify="spaced-start" verticalSpacing="0.5rem">
								<div
									className={isWide ? generalStyles["field-area-container"] : undefined}
									style={{ paddingBottom: isWide ? 0 : undefined }}
								>
									<ReEnrollmentStudentsField
										formState={formState}
										students={students}
										classYears={classYears}
										isWide={isWide}
										style={{ width: "100%" }}
									/>
								</div>

								<span className={generalStyles["notice"]}>
									<span className={generalStyles["notice__asterisk"]}>*</span>Price reflects tuition and any applicable
									discounts.
								</span>
							</Column>
						</Section.Body>
					</Section>

					<Section>
						<Section.Header>
							<Heading.H3 noMargin>Other Options</Heading.H3>
						</Section.Header>
						<Section.Body>
							<Column justify="spaced-start" verticalSpacing="0.5rem">
								<div className={isWide ? generalStyles["field-area-container"] : undefined}>
									<ReEnrollmentMealPlanContent isWide={isWide}>
										<StudentEnrollmentIntentField.IsInterestedInMealPlan
											formState={formState}
											noHeader
											style={{ width: "fit-content" }}
										/>
									</ReEnrollmentMealPlanContent>
								</div>

								<span className={generalStyles["notice"]}>
									<span className={generalStyles["notice__asterisk"]}>*</span>You'll have the chance to select meal
									plans for each student later.
								</span>
							</Column>
						</Section.Body>
					</Section>

					<If condition={!isWide}>
						<Row justify="center">
							<SubmitButton
								onClick={formState.onSubmitWrapper(onSubmit)}
								submissionStatus={formState.submissionStatus}
								style={{ paddingLeft: "3rem", paddingRight: "3rem" }}
							>
								Submit
							</SubmitButton>
						</Row>
					</If>
				</Form>
			</Modal.Body>
			<If condition={isWide}>
				<Modal.Footer className={generalStyles["modal__footer"]}>
					<Row justify="space-between">
						<Conditional>
							<If condition={anyFieldChanged(formState)}>
								<ButtonWithConfirmation
									variant="link"
									onClick={onCancel}
									confirmationContent="Are you sure you want to close this? Your changes will not be saved."
								>
									Cancel
								</ButtonWithConfirmation>
							</If>
							<Else>
								<Button variant="link" onClick={onCancel}>
									Cancel
								</Button>
							</Else>
						</Conditional>

						<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
							Submit
						</SubmitButton>
					</Row>
				</Modal.Footer>
			</If>
		</React.Fragment>
	);
}

function useCreateOrUpdate() {
	const [create] = useCreateOrUpdateBulkStudentEnrollmentIntentsMutation();
	return React.useCallback(
		async (formValues: ReEnrollmentFormValues) => {
			const formValuesSets = toStudentEnrollmentIntentCreateFormValues(formValues);
			const inputs = formValuesSets.map(StudentEnrollmentIntentFormConversion.toGQLCreateInput);
			const { data, errors } = await create({
				variables: { inputs },
				update: (cache) => {
					cache.evict({ id: "ROOT_QUERY", fieldName: "studentEnrollmentIntentConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "studentConnection" });
				},
			});
			return { data: data?.createOrUpdateBulkStudentEnrollmentIntents ?? null, errors: errors ?? null };
		},
		[create],
	);
}

function toStudentEnrollmentIntentCreateFormValues(formValues: ReEnrollmentFormValues) {
	return formValues.students.map((student) => ({
		...student,
		academicYearID: formValues.academicYearID,
		isInterestedInMealPlan: formValues.isInterestedInMealPlan,
	}));
}

const blankReEnrollmentStudentFormValues: ReEnrollmentStudentFormValues = {
	studentID: null,
	isEnrolling: false,
	declineReason: null,
	declineReasonDescription: "",
};

type ReEnrollmentStudentsFieldProps = {
	formState: FormState<Pick<ReEnrollmentFormValues, "students">>;
	students: (Pick<Student, "id" | "graduationYear"> & StudentNameAndImageFragment)[];
	classYears: Record<number, ClassYearNameFragment>;
	isWide: boolean;
} & Partial<ClassNameProps & StyleProps>;

function ReEnrollmentStudentsField({
	formState,
	students,
	classYears,
	isWide,
	className,
	style,
}: ReEnrollmentStudentsFieldProps) {
	const Items = React.useCallback(
		(props: SubFormFieldItemsProps<Pick<ReEnrollmentFormValues, "students">, ReEnrollmentStudentFormValues>) => (
			<ReEnrollmentStudentsField.Items {...props} isWide={isWide} />
		),
		[isWide],
	);

	const SubForm = React.useCallback(
		(props: SubFormRenderProps<ReEnrollmentStudentFormValues>) => {
			const student = students.find((student) => student.id === props.formState.formValues.studentID);
			if (!student) {
				// TODO more graceful error handling
				return <span>Error</span>;
			}
			return <ReEnrollmentStudentsField.SubForm {...props} student={student} classYears={classYears} isWide={isWide} />;
		},
		[students, classYears, isWide],
	);

	return (
		<SubFormField
			formState={formState}
			name="students"
			blankItem={blankReEnrollmentStudentFormValues}
			noHeader
			Items={Items}
			className={className}
			style={
				{
					"--sub-form-field__value---padding": 0,
					"--sub-form-field__value---font-size": "1rem",
					...style,
				} as React.CSSProperties
			}
		>
			{SubForm}
			{ReEnrollmentStudentsField.AddButton}
		</SubFormField>
	);
}

type ReEnrollmentStudentsFieldItemsProps = SubFormFieldItemsProps<
	Pick<ReEnrollmentFormValues, "students">,
	ReEnrollmentStudentFormValues
> & {
	isWide: boolean;
};

ReEnrollmentStudentsField.Items = function ({ children, isWide }: ReEnrollmentStudentsFieldItemsProps) {
	if (isWide) {
		// TODO hex-core-update simplify disabling internal scroll
		return (
			<Table
				columns={tableColumns}
				columnWidths={tableColumnWidths}
				style={{ overflow: "scroll", "--table__header---top": 0 } as React.CSSProperties}
			>
				<Table.Header style={{ overflow: "visible" }}>
					<Table.Row className={generalStyles["students-table__header__row"]}>
						<Table.Heading column="icon" noResize></Table.Heading>
						<Table.Heading column="studentName" noResize>
							Student
						</Table.Heading>
						<Table.Heading column="currentYear" noResize>
							Current Year
						</Table.Heading>
						<Table.Heading column="nextYear" noResize>
							Next Year
						</Table.Heading>
						<Table.Heading column="tuition" noResize>
							Tuition
						</Table.Heading>
						<Table.Heading column="isEnrolling" noResize style={{ textAlign: "right" }}>
							Re-Enroll?
						</Table.Heading>
					</Table.Row>
				</Table.Header>
				<Table.Body style={{ overflow: "visible" }}>{children}</Table.Body>
			</Table>
		);
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1rem">
			{children}
		</Column>
	);
};

type ReEnrollmentStudentsFieldSubFormProps = SubFormRenderProps<ReEnrollmentStudentFormValues> & {
	student: Pick<Student, "id" | "graduationYear"> & StudentNameAndImageFragment;
	classYears: Record<number, ClassYearNameFragment>;
	isWide: boolean;
};

ReEnrollmentStudentsField.SubForm = function ({
	formState,
	index,
	numItems,
	student,
	classYears,
	isWide,
}: ReEnrollmentStudentsFieldSubFormProps) {
	useInternalField(formState, "studentID");

	const classYear = StudentUtilities.getClassYear(student, classYears);
	const nextClassYear = StudentUtilities.getNextClassYear(student, classYears);

	const isLast = index === numItems - 1;

	const tuitionText = useStudentTuitionText(student.id);

	useInternalField(formState, "declineReason");

	if (isWide) {
		return (
			<React.Fragment>
				<Table.Row
					className={generalStyles["students-table__body__row"]}
					style={{ "--table__cell---padding": "1rem 0.5rem", lineHeight: "1.75" } as React.CSSProperties}
				>
					<Table.Cell column="icon" style={{ paddingTop: 0, paddingBottom: 0 }}>
						<Row justify="center" align="center" style={{ height: "100%" }}>
							<StudentIcon student={student} size={32} />
						</Row>
					</Table.Cell>
					<Table.Cell column="studentName" oneLine>
						{StudentFormat.name(student)}
					</Table.Cell>
					<Table.Cell column="currentYear" oneLine>
						{classYear ? ClassYearFormat.name(classYear) : "—"}
					</Table.Cell>
					<Table.Cell column="nextYear" oneLine>
						{nextClassYear ? ClassYearFormat.name(nextClassYear) : "—"}
					</Table.Cell>
					<Table.Cell column="tuition" oneLine style={{ fontWeight: "bold" }}>
						{tuitionText}
					</Table.Cell>
					<Table.Cell column="isEnrolling" style={{ paddingTop: 0, paddingBottom: 0 }}>
						<Row justify="center" align="center" style={{ height: "100%" }}>
							<StudentEnrollmentIntentField.IsEnrolling
								formState={formState}
								noHeader
								style={{ width: "fit-content", height: "fit-content" }}
							/>
						</Row>
					</Table.Cell>
				</Table.Row>
				<FieldIf formState={formState} name="declineReasonDescription" condition={!formState.formValues.isEnrolling}>
					<div className={generalStyles["decline-reasons"]} style={{ marginBottom: "1rem" }}>
						<StudentEnrollmentIntentField.DeclineReasonDescription
							formState={formState}
							label="Reason for Leaving"
							style={{ width: "100%" }}
						/>
					</div>
				</FieldIf>
				<If condition={!isLast}>
					<Rule />
				</If>
			</React.Fragment>
		);
	}

	return (
		<div className={generalStyles["full-tile"]}>
			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.5rem"
				className={generalStyles["student-table__header"]}
			>
				<StudentIcon student={student} size={40} />
				<TruncatedText className={generalStyles["student-table__name"]}>{StudentFormat.name(student)}</TruncatedText>
			</Row>

			<Column className={generalStyles["student-table__body"]}>
				<Row justify="space-between" className={generalStyles["student-table__row"]}>
					<span className={generalStyles["student-table__cell--label"]}>Current Year:</span>
					<span className={generalStyles["student-table__cell--value"]}>
						{classYear ? ClassYearFormat.name(classYear) : "—"}
					</span>
				</Row>
				<Row justify="space-between" className={generalStyles["student-table__row"]}>
					<span className={generalStyles["student-table__cell--label"]}>Next Year:</span>
					<span className={generalStyles["student-table__cell--value"]}>
						{nextClassYear ? ClassYearFormat.name(nextClassYear) : "—"}
					</span>
				</Row>
				<Row justify="space-between" className={generalStyles["student-table__row"]}>
					<span className={generalStyles["student-table__cell--label"]}>Tuition:</span>
					<span className={generalStyles["student-table__cell--value"]}>{tuitionText}</span>
				</Row>
				<Row
					justify="space-between"
					className={generalStyles["student-table__row"] + " " + generalStyles["student-table__row--is-enrolling"]}
				>
					<span className={generalStyles["student-table__cell--label"]}>Re-Enroll?</span>
					<span className={generalStyles["student-table__cell--value"]}>
						<StudentEnrollmentIntentField.IsEnrolling
							formState={formState}
							noHeader
							style={{ width: "fit-content", height: "fit-content" }}
						/>
					</span>
				</Row>
				<FieldIf formState={formState} name="declineReasonDescription" condition={!formState.formValues.isEnrolling}>
					<div className={generalStyles["decline-reasons"]}>
						<StudentEnrollmentIntentField.DeclineReasonDescription
							formState={formState}
							label="Reason for Leaving"
							style={{ width: "100%" }}
						/>
					</div>
				</FieldIf>
			</Column>
		</div>
	);
};

ReEnrollmentStudentsField.AddButton = function () {
	return null;
};

function getInitialReEnrollmentFormValues(
	students: ReEnrollmentFormProps["students"],
	academicYear: ReEnrollmentFormProps["academicYear"],
	classYears: ReEnrollmentFormProps["classYears"],
): ReEnrollmentFormValues {
	const isInterestedInMealPlan = students.some((student) =>
		student.studentEnrollmentIntents.some((intent) => intent.isInterestedInMealPlan),
	);
	return {
		academicYearID: academicYear.id,
		isInterestedInMealPlan: isInterestedInMealPlan,
		students: students.map((student) => {
			if (student.studentEnrollmentIntents.length > 0) {
				const studentEnrollmentIntent = student.studentEnrollmentIntents[0];
				return {
					...StudentEnrollmentIntentFormConversion.toFormValues({
						studentEnrollmentIntent,
						fields: {
							isEnrolling: true,
							declineReason: true,
							declineReasonDescription: true,
						},
					}),
					studentID: student.id,
				};
			}

			const baseFormValues = pickKeys(StudentEnrollmentIntentFormValues.initialCreate, [
				"isEnrolling",
				"declineReason",
				"declineReasonDescription",
			]);

			const nextClassYear = StudentUtilities.getNextClassYear(student, classYears);
			if (!nextClassYear) {
				return {
					...baseFormValues,
					isEnrolling: false,
					studentID: student.id,
					declineReasonDescription: "Graduation",
				};
			}

			return {
				...baseFormValues,
				isEnrolling: true,
				studentID: student.id,
			};
		}),
	};
}
